
import React, { useState } from 'react';
import { AIEvent, Theme } from '../types';
import { CATEGORIES, PRIORITY_LEVELS } from '../constants';

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (event: Omit<AIEvent, 'id' | 'addedAt'>) => Promise<void>;
  theme: Theme;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ isOpen, onClose, onSave, theme }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    campaignName: '',
    category: CATEGORIES[0],
    url: '',
    priority: '',
    startDate: '',
    endDate: '',
    location: '',
    focusArea: '',
    information: '',
    attendeeSize: '',
    targetAudience: ''
  });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const date = new Date(formData.startDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;

      await onSave({
        ...formData,
        year,
        month,
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const inputStyle = `w-full bg-transparent border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-accent transition-all ${
    theme === 'dark' ? 'border-white/10 text-white placeholder:text-white/20' : 'border-black/10 text-slate-800 placeholder:text-slate-400 bg-slate-50'
  }`;

  const labelStyle = "text-[10px] font-bold text-slate-400 dark:text-white/30 uppercase tracking-widest mb-1.5 block ml-1";

  const optionClassName = theme === 'dark' ? 'bg-[#1a1a1a] text-white' : 'bg-white text-slate-900';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
      <div 
        className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[2.5rem] border shadow-2xl animate-in zoom-in-95 duration-300 ${
          theme === 'dark' ? 'bg-[#0a0a0a] border-white/10' : 'bg-white border-black/5'
        }`}
      >
        <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-8">
          <div className="flex justify-between items-start">
            <div>
              <h2 className={`text-2xl font-black tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>New Intelligence Record</h2>
              <p className="text-sm text-slate-400 dark:text-white/40 mt-1">Manually enter a new event into the global intelligence database.</p>
            </div>
            <button 
              type="button" 
              onClick={onClose} 
              className="p-2 text-slate-400 hover:text-red-500 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className={labelStyle}>EVENT NAME</label>
              <input 
                required
                className={inputStyle}
                placeholder="e.g. NeurIPS 2025"
                value={formData.campaignName}
                onChange={e => setFormData({...formData, campaignName: e.target.value})}
              />
            </div>

            <div>
              <label className={labelStyle}>CATEGORY</label>
              <select 
                className={inputStyle}
                value={formData.category}
                onChange={e => setFormData({...formData, category: e.target.value})}
              >
                {CATEGORIES.map(c => <option key={c} value={c} className={optionClassName}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className={labelStyle}>PRIORITY RANK</label>
              <select 
                className={inputStyle}
                value={formData.priority}
                onChange={e => setFormData({...formData, priority: e.target.value})}
              >
                <option value="" className={optionClassName}>Unassigned</option>
                {PRIORITY_LEVELS.map(p => <option key={p} value={p} className={optionClassName}>{p}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className={labelStyle}>OFFICIAL URL</label>
              <input 
                type="text"
                className={inputStyle}
                placeholder="https://event-website.com (Optional)"
                value={formData.url}
                onChange={e => setFormData({...formData, url: e.target.value})}
              />
            </div>

            <div>
              <label className={labelStyle}>START DATE</label>
              <input 
                required
                type="date"
                className={inputStyle}
                value={formData.startDate}
                onChange={e => setFormData({...formData, startDate: e.target.value})}
              />
            </div>

            <div>
              <label className={labelStyle}>END DATE</label>
              <input 
                required
                type="date"
                className={inputStyle}
                value={formData.endDate}
                onChange={e => setFormData({...formData, endDate: e.target.value})}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelStyle}>LOCATION</label>
              <input 
                required
                className={inputStyle}
                placeholder="City, Country or Online"
                value={formData.location}
                onChange={e => setFormData({...formData, location: e.target.value})}
              />
            </div>

            {/* New Fields */}
            <div>
              <label className={labelStyle}>AUDIENCE SIZE</label>
              <input 
                className={inputStyle}
                placeholder="e.g. 5000+"
                value={formData.attendeeSize}
                onChange={e => setFormData({...formData, attendeeSize: e.target.value})}
              />
            </div>

             <div>
              <label className={labelStyle}>TARGET AUDIENCE</label>
              <input 
                className={inputStyle}
                placeholder="e.g. Developers"
                value={formData.targetAudience}
                onChange={e => setFormData({...formData, targetAudience: e.target.value})}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelStyle}>EVENT FOCUS</label>
              <input 
                required
                className={inputStyle}
                placeholder="Short summary of key topics..."
                value={formData.focusArea}
                onChange={e => setFormData({...formData, focusArea: e.target.value})}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelStyle}>EVENT OVERVIEW</label>
              <textarea 
                required
                className={`${inputStyle} h-32 resize-none`}
                placeholder="Detailed information about the event track, speakers, etc..."
                value={formData.information}
                onChange={e => setFormData({...formData, information: e.target.value})}
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button 
              type="button"
              onClick={onClose}
              className={`flex-1 px-8 py-4 font-bold rounded-2xl border transition-all ${
                theme === 'dark' ? 'border-white/10 text-white/40 hover:text-white' : 'border-black/5 text-slate-400 hover:text-slate-700'
              }`}
            >
              Discard
            </button>
            <button 
              type="submit"
              disabled={isSaving}
              className="flex-[2] bg-accent text-black font-bold py-4 rounded-2xl shadow-xl shadow-accent/10 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving Record...</span>
                </>
              ) : (
                'Save to Database'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventModal;